import { ClickAwayListener, Popper } from "@mui/base";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";

import { UserNotification } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { queryKeys } from "~/api/queryKeys";
import { Icon } from "~/assets/icons/Icon";
import Spinner from "~/components/ui/spinner/Spinner";
import { useTeam } from "~/contexts/TeamProvider";
import { formatLocalDate } from "~/helpers/formatDate";
import { notificationPath } from "~/helpers/notificationPath";
import { parsePaginationHeaders } from "~/helpers/paginationHeaders";
import { useImpersonation } from "~/hooks/useImpersonation";
import { useStagedBoolean } from "~/hooks/useStagedBoolean";

import sidebarStyle from "../DashboardSidebar.module.scss";
import styles from "./NotificationsPopup.module.scss";

const PAGE_SIZE = 15;
const CACHE_TTL = 10 * 60 * 1000;

type Props = {
  isSidebarOpen: boolean;
  unreadCount: number;
};

const NotificationsPopup = ({ isSidebarOpen, unreadCount }: Props) => {
  const { isImpersonating } = useImpersonation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [mode, setMode] = useState<"all" | "unread">("all");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const isUnreadMode = mode === "unread" && unreadCount > 0;
  const queryClient = useQueryClient();
  const { teamId } = useTeam();
  const isOpen = Boolean(anchorEl);
  const listRef = useRef<HTMLDivElement | null>(null);
  // useEffect([isOpen]) can't restore scroll because the Popper portal mounts children
  // after the effect fires — listRef.current is still null at that point.
  // A callback ref fires synchronously when the node is added to the DOM, so scrollTop
  // is set before the first paint.
  const listRefCallback = useCallback((node: HTMLDivElement | null) => {
    listRef.current = node;
    if (node) node.scrollTop = savedScrollTop.current;
  }, []);
  const pendingScroll = useRef<{
    scrollHeight: number;
    wasScrollable: boolean;
  } | null>(null);
  const savedScrollTop = useRef<number>(0);
  const cacheCleanupTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: queryKeys.notifications(teamId!, isUnreadMode),
      queryFn: async ({ pageParam = 1 }) => {
        const { data, headers } =
          await apiClient.notifications.notificationsList({
            s: "created_at DESC",
            "page[size]": PAGE_SIZE,
            "page[number]": pageParam as number,
            ...(isUnreadMode && ({ "q[read_at_null]": 1 } as object)),
          });
        const { nextPage } = parsePaginationHeaders(headers);
        const items = (
          data as unknown as { user_notifications: UserNotification[] }
        ).user_notifications;

        return { items, nextPage };
      },
      initialPageParam: 1,
      getNextPageParam: (page) => page.nextPage ?? undefined,
      enabled: isOpen && teamId != null,
      staleTime: Infinity,
    });

  const items = data?.pages.flatMap((p) => p.items) ?? [];

  const skeletonVisible = useStagedBoolean(isLoading, 0, 1000);

  useEffect(() => {
    if (!pendingScroll.current || !listRef.current) return;

    const { scrollHeight, wasScrollable } = pendingScroll.current;
    pendingScroll.current = null;
    // only scroll if content was overflowing the container before load —
    // if it wasn't scrollable (e.g. only 1-2 items or empty list), new items are visible from the top
    if (wasScrollable) {
      listRef.current.scrollTo({ top: scrollHeight, behavior: "smooth" });
    }
  }, [data]);

  const { mutate: markAllRead, isPending: isMarkingAll } = useMutation({
    mutationFn: async () => {
      if (isImpersonating) return Promise.resolve();

      await apiClient.notifications.seenCreate();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.notificationsRoot(teamId!),
      });
    },
  });

  const { mutate: markOneRead } = useMutation({
    mutationFn: async (id: number) => {
      if (isImpersonating) return Promise.resolve();

      await apiClient.notifications.seenCreate2(id);
    },
    onMutate: (id) => {
      if (isImpersonating) return;

      const allKey = queryKeys.notifications(teamId!, false);
      const unreadKey = queryKeys.notifications(teamId!, true);

      queryClient.setQueryData<typeof data>(
        allKey,
        (prev) =>
          prev && {
            ...prev,
            pages: prev.pages.map((page) => ({
              ...page,
              items: page.items.map((n) =>
                n.id === id ? { ...n, read_at: new Date().toISOString() } : n,
              ),
            })),
          },
      );

      // in unread mode: set read_at (removes blue highlight) but keep item in the list
      // so the user can still read the expanded description; items are removed from the
      // unread cache on popup close via invalidateQueries
      queryClient.setQueryData<typeof data>(
        unreadKey,
        (prev) =>
          prev && {
            ...prev,
            pages: prev.pages.map((page) => ({
              ...page,
              items: page.items.map((n) =>
                n.id === id ? { ...n, read_at: new Date().toISOString() } : n,
              ),
            })),
          },
      );
    },
    onError: () => {
      // on error invalidate both caches so server state is the source of truth;
      // snapshot-based rollback is unsafe with multiple parallel mutations
      if (isImpersonating) return;

      queryClient.invalidateQueries({
        queryKey: queryKeys.notificationsRoot(teamId!),
      });
    },
  });

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    if (cacheCleanupTimer.current !== null) {
      clearTimeout(cacheCleanupTimer.current);
      cacheCleanupTimer.current = null;
    }
    setAnchorEl(e.currentTarget);
  };

  // remove items marked read during this session from the unread cache —
  // they were kept visible while expanded, now clean up locally without a network request
  const flushUnreadCache = () => {
    if (teamId == null) return;
    queryClient.setQueryData<typeof data>(
      queryKeys.notifications(teamId, true),
      (prev) =>
        prev && {
          ...prev,
          pages: prev.pages.map((page) => ({
            ...page,
            items: page.items.filter((n) => !n.read_at),
          })),
        },
    );
  };

  const handleClose = () => {
    savedScrollTop.current = listRef.current?.scrollTop ?? 0;
    flushUnreadCache();
    // drop the cache after CACHE_TTL so stale data isn't shown on next open
    cacheCleanupTimer.current = setTimeout(() => {
      savedScrollTop.current = 0;
      setMode("all");
      if (teamId != null)
        queryClient.removeQueries({
          queryKey: queryKeys.notificationsRoot(teamId),
        });
    }, CACHE_TTL);
    setAnchorEl(null);
  };

  const handleLoadMore = () => {
    pendingScroll.current = {
      scrollHeight: listRef.current?.scrollHeight ?? 0,
      // scrollHeight = total content height (including overflow); clientHeight = visible container height
      // scrollHeight > clientHeight means content overflows and a scrollbar is active
      wasScrollable:
        (listRef.current?.scrollHeight ?? 0) >
        (listRef.current?.clientHeight ?? 0),
    };
    fetchNextPage();
  };

  const handleItemClick = (item: UserNotification) => {
    if (!isImpersonating && !item.read_at) markOneRead(item.id);

    setExpandedId(expandedId === item.id ? null : item.id);
  };

  const handleItemLinkClick = (item: UserNotification) => {
    if (!isImpersonating && !item.read_at) markOneRead(item.id);

    handleClose();
  };

  return (
    <>
      <div
        className={clsx(sidebarStyle.menuLink, styles.trigger, {
          [styles.active]: isOpen,
        })}
        onClick={handleOpen}
      >
        {unreadCount > 0 && !isSidebarOpen && (
          <span className={styles.badgeClosed}>
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
        <span className={sidebarStyle.menuLinkIconTitle}>
          <span className={sidebarStyle.menuIconTitle}>
            <span className={styles.iconWrapper}>
              <Icon
                iconName="NotificationIcon"
                width="1.5rem"
                height="1.5rem"
                hover
              />
            </span>
            {isSidebarOpen && (
              <span className={styles.labelWithBadge}>
                <span className={sidebarStyle.label}>Notifications</span>
                {unreadCount > 0 && (
                  <span className={styles.badge}>
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </span>
            )}
          </span>
        </span>
      </div>

      <Popper
        open={isOpen}
        anchorEl={anchorEl}
        placement="right-end"
        style={{ zIndex: 9999 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <div className={styles.popup}>
            <div className={styles.header}>
              <div className={styles.headerTop}>
                <span className={styles.headerTitle}>Notifications</span>
                {unreadCount > 0 && !isImpersonating && (
                  <button
                    className={styles.markAllBtn}
                    onClick={() => markAllRead()}
                    disabled={isMarkingAll}
                  >
                    Mark all read
                  </button>
                )}
              </div>
              {unreadCount > 0 && (
                <div className={styles.headerTabs}>
                  <button
                    className={clsx(styles.tab, {
                      [styles.tabActive]: !isUnreadMode,
                    })}
                    onClick={() => {
                      flushUnreadCache();
                      setMode("all");
                    }}
                  >
                    All
                  </button>
                  <button
                    className={clsx(styles.tab, {
                      [styles.tabActive]: isUnreadMode,
                    })}
                    onClick={() => setMode("unread")}
                  >
                    Unread ({unreadCount})
                  </button>
                </div>
              )}
            </div>

            <div className={styles.list} ref={listRefCallback}>
              {skeletonVisible ? (
                Array.from({ length: 7 }, (_, i) => (
                  <div key={i} className={styles.skeletonItem}>
                    <Skeleton circle width={8} height={8} />
                    <div className={styles.skeletonContent}>
                      <Skeleton width="90%" height={14} />
                      <Skeleton width="35%" height={12} />
                    </div>
                  </div>
                ))
              ) : items.length === 0 ? (
                <div className={styles.center}>
                  <p className={styles.empty}>No notifications yet</p>
                </div>
              ) : (
                items.map((item) => (
                  <div
                    key={item.id}
                    className={clsx(styles.item, {
                      [styles.unread]: !item.read_at,
                    })}
                    onClick={() => handleItemClick(item)}
                  >
                    <span
                      className={clsx(styles.dot, {
                        [styles.dotHidden]: !!item.read_at,
                      })}
                    />
                    <div className={styles.itemContent}>
                      <p className={styles.itemTitle}>
                        {item.notification?.title}
                      </p>
                      {expandedId === item.id && item.notification?.message && (
                        <p className={styles.itemMessage}>
                          {item.notification.message}
                        </p>
                      )}
                      <p className={styles.itemDate}>
                        {formatLocalDate(item.created_at)}
                      </p>
                    </div>
                    {item.notification && (
                      <Link
                        to={notificationPath(item.notification)}
                        className={styles.itemLink}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleItemLinkClick(item);
                        }}
                      >
                        <Icon
                          iconName="ExternalLinkIcon"
                          width="0.75rem"
                          height="0.75rem"
                        />
                      </Link>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className={styles.footer}>
              {hasNextPage && (
                <button
                  className={clsx(styles.loadMoreBtn, {
                    [styles.loadMoreBtnLoading]: isFetchingNextPage,
                  })}
                  onClick={handleLoadMore}
                  disabled={isFetchingNextPage}
                >
                  <span
                    className={clsx(styles.loadMoreSpinner, {
                      [styles.loadMoreSpinnerVisible]: isFetchingNextPage,
                    })}
                  >
                    <Spinner size="0.75rem" />
                  </span>
                  Load older
                </button>
              )}
            </div>
          </div>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default NotificationsPopup;
